import React, { FC, Fragment, useEffect, useState, useContext } from 'react';
import axios, { CancelTokenSource } from 'axios';
import {
  Grid,
  Container,
  Typography,
  makeStyles,
  Button,
  TextField,
  CircularProgress,
  Theme,
  FormHelperText,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Fade,
  RadioGroup,
  Radio,
  useTheme,
  useMediaQuery
} from '@material-ui/core';
import { Page, StandardConfirmationDialog, PaperCustom, Breadcrumb, CardCustomer } from 'components';
import StandardConfirmationDialogGiro from 'components/StandardConfirmationDialogGiro';
import {
  PARTNER_SEARCH_BASE_URL,
  INVOICE_BASE_URL,
  INVOICE_PAYMENT_BASE_URL,
  GET_INVOICE_PAYMENT_DETAIL_BASE_URL,
  BANK_BASE_URL,
  RETURN_INVOICE_BASE_URL,
  PARTNER_DETAIL_BASE_URL,
  GET_INVOICE_PAYMENT_RETURN_BASE_URL,
  RETURN_BILL_BASE_URL,
  GET_DETAIL_BANK_BASE_URL
} from 'constants/url';
import { format } from 'date-fns';
import { dummyPartner, dummyBank } from 'utils/dummy';
import { BACKGROUND, BLUE_PRIMARY, GRAY_3, GREEN, GREY, PRIMARY_MOBILE, RED_MOBILE, WHITE, YELLOW_MOBILE } from 'constants/colors';
import { diff, sortByInvoiceKey } from 'utils';
import { CurrentUserContext } from 'contexts/CurrentUserContext';
import useRouter from 'hooks/useRouter';
import Autocomplete, { AutocompleteInputChangeReason } from '@material-ui/lab/Autocomplete';
import NumberFormat from 'components/NumberFormat';
import PaymentMethod from 'typings/enum/PaymentMethod';
import PaymentStatus from 'typings/enum/PaymentStatus';
import InvoiceModal from './components/InvoiceModal';
import InvoiceTable from './components/InvoiceTable';
import InvoiceReturnTable from './components/InvoiceReturnTable';
import InvoiceReturnModal from './components/InvoiceReturnModal';
import ConfirmPaymentModal from './components/ConfirmPaymentModal';
import NumberFormatMask from 'components/NumberFormatMask';
import GiroTable from 'pages/PurchasePaymentUpdatePage/components/GiroTable';
import GiroModal from 'pages/PurchasePaymentUpdatePage/components/GiroModal/GiroModal';
import _ from 'lodash';
import { useDropzone, FileWithPath } from 'react-dropzone';
import { AddRounded, CloseRounded, CloudUpload } from '@material-ui/icons';
import { Alert, AlertTitle } from '@material-ui/lab';

const useStyles = makeStyles((theme: Theme) => ({
  backdrop: {
    zIndex: theme.zIndex.drawer + 1,
    color: '#fff'
  },
  refresh: {
    backgroundColor: GREEN,
    color: WHITE,
    '&:hover': {
      backgroundColor: GREEN
    }
  },
  warningWrapper: {
    margin: '1em 0',
    padding: '.4em'
  },
  warningContent: {
    background: YELLOW_MOBILE,
    padding: '.8em 1em',
    borderRadius: '.4em',
    '& > *': {
      color: WHITE
    }
  },
  dropZone: {
    margin: '0 0.4em',
    padding: '1em',
    borderRadius: 2,
    border: `2px dashed ${BLUE_PRIMARY} `,
    backgroundColor: BACKGROUND,
    color: GRAY_3,
    outline: 'none',
    transition: 'border .24s ease-in-out',
    verticalAlign: 'middle',
    textAlign: 'center'
  },
  thumbsContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap'
  },
  thumb: {
    position: 'relative',
    display: 'inline-flex',
    borderRadius: 2,
    border: '1px solid #eaeaea',
    marginRight: 20,
    width: 150,
    height: 150,
    boxSizing: 'border-box'
  },
  closeIcon: {
    position: 'absolute',
    top: -10,
    right: -10,
    fontSize: '2rem',
    backgroundColor: '#fff',
    borderRadius: '10em',
    cursor: 'pointer',
    boxShadow: '0 2px 5px rgb(0 0 0 / 14%)'
  },
  addIcon: {
    fontSize: '4rem',
    backgroundColor: '#fff',
    borderRadius: '10em',
    cursor: 'pointer',
    boxShadow: '0 2px 5px rgb(0 0 0 / 14%)'
  },
  thumbInner: {
    display: 'flex',
    minWidth: 0,
    overflow: 'hidden'
  },
  img: {
    display: 'block',
    width: 'auto',
    height: '100%'
  },
  iconUpload: {
    fontSize: 100,
    color: BLUE_PRIMARY
  }
}));

const PaymentCreatePage: FC = () => {
  const classes = useStyles();
  const { history, location, match } = useRouter();
  const { currentUser } = useContext(CurrentUserContext);
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.between('xs', 'sm'));

  // eslint-disable-next-line
  const { id } = match.params;

  const [snackbarVariant, setSnackbarVariant] = useState<'success' | 'error'>('success');
  const [message, setMessage] = useState<string>('');
  const [openSnackbar, setOpenSnackbar] = useState<boolean>(false);
  const [openSnackbarGiro, setOpenSnackbarGiro] = useState<boolean>(false);

  const [partner, setPartner] = useState<PartnerModel>(dummyPartner);
  const [partners, setPartners] = useState<PartnerModel[]>([dummyPartner]);

  const [queryString, setQueryString] = useState<string>('');
  const [returnNumber, setReturnNumber] = useState<string>('');
  const [partnerName, setPartnerName] = useState<string>('');
  const [count, setCount] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [order, setOrder] = useState<'asc' | 'desc'>('asc');
  const [orderBy, setOrderBy] = useState<string>('id');

  const [paymentId, setPaymentId] = useState<number>(0);
  const [payDate, setPayDate] = useState<Date>(new Date());
  const [transferDate, setTransferDate] = useState<Date>(new Date());
  const [paymentMethod, setPaymentMethod] = useState<string>('');
  const [giroBank, setGiroBank] = useState<string>('');
  const [giroNumber, setGiroNumber] = useState<string>('');
  const [giroAmount, setGiroAmount] = useState<number>(0);
  const [giroDueDate, setGiroDueDate] = useState<Date>(new Date());
  const [note, setNote] = useState<string>('');
  const [fullPayment, setFullPayment] = useState<boolean>(false);
  const [useReturn, setUseReturn] = useState<boolean>(false);
  const [totalPay, setTotalPay] = useState<number>(0);

  const [otherPiece, setOtherPiece] = useState<number>(0);
  const [currentTotalPay, setCurrentTotalPay] = useState<number>(0);
  const [currentTotalReturn, setCurrentTotalReturn] = useState<number>(0);
  const [totalPrice, setTotalPrice] = useState<number>(0);
  const [totalReturn, setTotalReturn] = useState<number>(0);

  const [image, setImage] = useState<{ path: string }[]>([]);
  const [imageBlob, setImageBlob] = useState<FileWithPath[]>([]);
  const [imageUrl, setImageUrl] = useState<string[]>([]);
  const [isLoadingData, setIsLoadingData] = useState<boolean>(false);
  const [isLoadingReturn, setLoadingReturn] = useState<boolean>(false);
  const [loadPartner, setLoadPartner] = useState<boolean>(false);
  const [loadingBank, setLoadingBank] = useState<boolean>(false);
  const [otherPayments, setOtherPayments] = useState<OtherPayment[]>([]);
  const [payload, setPayload] = useState<PayloadPayment | null>(null);

  const [isUpdate, setUpdate] = useState<boolean>(false);
  const [isSubmit, setSubmit] = useState<boolean>(false);
  const [isFullReturn, setFullReturn] = useState<boolean>(false);

  const [totalPayError, setTotalPayError] = useState<string>('');
  const [paymentMessageError, setPaymentMessageError] = useState<string>('');
  const [paymentMethodMessage, setPaymentMethodMessage] = useState<string>('');
  const [paymentMethodImageMessage, setPaymentMethodImageMessage] = useState<string>('');
  const [bankMessage, setBankMessage] = useState<string>('');
  const [transferDateMessage, setTransferDateMessage] = useState<string>('');
  const [payDateMessage, setPayDateMessage] = useState<string>('');
  const [invoicePending, setInvoicePending] = useState<number[]>([]);

  const [giroBankMessage, setGiroBankMessage] = useState<string>('');
  const [giroNumberMessage, setGiroNumberMessage] = useState<string>('');
  const [giroAmountMessage, setGiroAmountMessage] = useState<string>('');
  const [giroDueDateMessage, setGiroDueDateMessage] = useState<string>('');
  const [giroMessage, setGiroMessage] = useState<string>('');

  const [openInvoice, setOpenInvoice] = useState<boolean>(false);
  const [invoices, setInvoices] = useState<(InvoiceModel & { type: 'return' | 'invoice' })[]>([]);
  const [invoiceReturn, setInvoiceReturn] = useState<InvoiceReturnModel[]>([]);

  const [checkedInvoice, setCheckedInvoice] = useState<(InvoiceModel & { type: 'return' | 'invoice' })[]>([]);
  const [checkedReturn, setCheckedReturn] = useState<InvoicePaymentModel[]>([]);
  const [checkedInvoiceReturn, setCheckedInvoiceReturn] = useState<InvoiceReturnModel[]>([]);

  const [partnerMessage, setPartnerMessage] = useState<string>('');
  const [openReturn, setOpenReturn] = useState<boolean>(false);
  const [openConfirm, setOpenConfirm] = useState<boolean>(false);
  const [isCreateGiro, setIsCreateGiro] = useState<boolean>(false);
  const [isRefresh, setRefresh] = useState<boolean>(false);
  const [disableMethod, setDisableMethod] = useState<boolean>(false);

  const [bank, setBank] = useState<BankModel>(dummyBank);
  const [banks, setBanks] = useState<BankModel[]>([dummyBank]);
  const [hasAutorisasi, setHasAutorisasi] = useState(false);
  const [giro, setGiro] = useState<GiroModalModel[]>([]);

  const cancelTokenSource: CancelTokenSource = axios.CancelToken.source();
  const { acceptedFiles, getRootProps, getInputProps } = useDropzone({
    accept: ['image/jpeg', 'image/png'],
    maxFiles: 5,
    onDrop: (acceptedFiles: FileWithPath[]) => {
      setImageUrl([]);
      setImage([]);
      setImageBlob([]);
      uploadImage(acceptedFiles);
    }
  });
  const uploadImage = (files: FileWithPath[]) => {
    files.map((value: any) => {
      setImageUrl([...imageUrl, URL.createObjectURL(value)]);
      setImage([...image, { path: `${partnerName}${new Date().getTime()}.${value.name.split('.').pop()}` }]);
      setImageBlob([...imageBlob, value]);
    });
  };

  const handleDeleteImage = (index: number): void => {
    setImageUrl(imageUrl.filter((val, key) => key !== index));
  };
  const handleSearchBank = async (value: string) => {
    const params = new URLSearchParams();
    params.append('keyword', value);
    try {
      setLoadingBank(true);
      const { data } = await axios.get(`${BANK_BASE_URL}?${params.toString()}`);
      setBanks(data.data);
    } catch (error) {
      console.log('error', error);
      handleSnackBar(true, 'error', 'Potongan telah gagal');
    } finally {
      setLoadingBank(false);
    }
  };

  const handleRequestSort = (event: React.MouseEvent<unknown>, property: string) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const fetchData = async () => {
    setIsLoadingData(true);

    const getQueryParams = () => {
      const params = new URLSearchParams();
      if (queryString) {
        params.append('number', queryString);
      }

      if (orderBy || order) {
        params.append('orderBy', orderBy);
        params.append('ordered', order);
      }

      if (partner) {
        params.append('partnerId', String(partner.id));
      }
      params.append('status', PaymentStatus.UNPAID);
      params.append('page', currentPage.toString());
      params.append('isPiece', 'true');
      if (!isUpdate) {
        params.append('draft', 'true');
      }
      return params.toString();
    };

    try {
      const { data: dataReturn } = await axios.get(`${RETURN_BILL_BASE_URL}?partnerId=${String(partner.id)}&status=UNPAID`, {
        cancelToken: cancelTokenSource.token
      });
      const { data } = await axios.get(`${INVOICE_BASE_URL}?${getQueryParams()}`, { cancelToken: cancelTokenSource.token });

      const newData = [
        ...dataReturn.data.map((value: any) => ({ ...value, type: 'return' })),
        ...data.data.map((value: any) => ({ ...value, type: 'invoice' }))
      ];
      setInvoices(newData);
      setCount(data.meta.last_page);
      setCurrentPage(data.meta.current_page);
    } catch (error) {
      console.log('error: ', error);
    } finally {
      setIsLoadingData(false);
    }
  };

  const fetchReturnInvoice = async () => {
    setLoadingReturn(true);

    const getQueryParams = () => {
      const params = new URLSearchParams();

      if (returnNumber) {
        params.append('number', returnNumber);
      }

      params.append('partnerId', partner.id.toString());
      params.append('typeReturn', 'CUSTOMER');
      params.append('usageStatus', 'false');
      return params.toString();
    };

    try {
      const { data } = await axios.get(`${RETURN_INVOICE_BASE_URL}?${getQueryParams()}`, { cancelToken: cancelTokenSource.token });
      setInvoiceReturn(data.data);
    } catch (error) {
      console.log('error: ', error);
    } finally {
      setLoadingReturn(false);
    }
  };

  const fetchCheckAutorisasi = async () => {
    if (partner && partner.id == 0) return;
    setHasAutorisasi(false);

    try {
      const { data } = await axios.get(`${INVOICE_PAYMENT_BASE_URL}/autorization-count/${partner.id}`, { cancelToken: cancelTokenSource.token });
      setHasAutorisasi(data.data.total > 0);
    } catch (error) {
      console.log('error: ', error);
    } finally {
    }
  };

  const fecthInvoicePayment = async () => {
    setIsLoadingData(true);
    setUpdate(true);

    try {
      const { data } = await axios.get(GET_INVOICE_PAYMENT_DETAIL_BASE_URL(+id));
      const payload = data.data.payload;
      if (payload) {
        setPaymentMethod(payload.paymentMethod);
        setPayDate(new Date(payload.payDate));
        setNote(payload.note);
        setTotalPay(payload.totalPay);
        setCurrentTotalPay(payload.totalPay);
        setFullReturn(payload.paymentMethod === PaymentMethod.RETURN);
        setUseReturn(payload.useReturn);
        setGiroBank(payload.giroBank);
        setTransferDate(new Date(payload.transferDate));
        setGiroDueDate(new Date(payload.giroDueDate));
        setGiroAmount(payload.giroAmount);
        setGiroNumber(payload.giroNumber);
        if (payload.paymentMethod === PaymentMethod.GIRO && payload.giroNumber && payload.giroAmount) {
          setGiro([
            {
              giroBank: payload.giroBank || '',
              giroNumber: payload.giroNumber,
              giroDueDate: payload.giroDueDate ? new Date(payload.giroDueDate) : new Date(),
              giroAmount: Number(payload.giroAmount)
            }
          ]);
        } else {
          setGiro([]);
        }
        setOtherPiece(payload.otherPiece);
        setPayload(data.data.payload);
        fetchAll(
          payload.PartnerId,
          payload.InvoiceId,
          payload.returns.length === 0 ? [0] : payload.returns.map((v: any) => v.ReturnId),
          payload.BankId ?? 0
        );
      } else {
        setIsLoadingData(false);
      }
    } catch (error) {
      console.log('error: ', error);
      setIsLoadingData(false);
    } finally {
    }
  };

  const fetchAll = (prtId: number, invoiceReferences: { id: number; type?: string }[], rtnId: number[], bankId: number) => {
    const invoiceIds = invoiceReferences.filter(value => (value.type || 'invoice') === 'invoice').map(value => value.id);
    const returnBillIds = invoiceReferences.filter(value => value.type === 'return').map(value => value.id);

    setLoadPartner(true);
    setLoadingBank(true);
    Promise.all([
      axios.get(PARTNER_DETAIL_BASE_URL(prtId)).catch(() => undefined),
      invoiceIds.length > 0 ? axios.get(`${INVOICE_BASE_URL}?ids=${invoiceIds.join(',')}&full=true`).catch(() => undefined) : Promise.resolve(undefined),
      returnBillIds.length > 0
        ? axios.get(`${RETURN_BILL_BASE_URL}?ids=${returnBillIds.join(',')}&full=true`).catch(() => undefined)
        : Promise.resolve(undefined),
      axios.get(`${RETURN_INVOICE_BASE_URL}?id=${rtnId.join(',')}`).catch(() => undefined),
      axios.get(GET_DETAIL_BANK_BASE_URL(bankId)).catch(() => undefined)
    ])
      .then(async (result: any) => {
        const [dataPartner, dataInvoice, dataReturnBill, dataReturn, dataBank] = result;
        let currentTotalPrice = 0;
        if (dataPartner) {
          setPartner(dataPartner.data.data);
          setPartners([dataPartner.data.data]);
        }

        if (dataInvoice || dataReturnBill) {
          const _invoices = [
            ...(dataInvoice ? dataInvoice.data.data.map((value: any) => ({ ...value, type: 'invoice' })) : []),
            ...(dataReturnBill ? dataReturnBill.data.data.map((value: any) => ({ ...value, type: 'return' })) : [])
          ];
          currentTotalPrice = _invoices.reduce((acc: any, curr: any) => acc + curr.totalPrice, 0);
          const sorted = sortByInvoiceKey(_invoices);
          setCheckedInvoice(sorted);
          // setInvoicePiece(dataInvoice.data.data.InvoicePiece);
        }

        if (dataReturn) {
          const _checkedReturn = dataReturn.data.data.map((item: any) => {
            return {
              id: 0,
              paymentNumber: '',
              PaymentId: null,
              ReturnId: item.id,
              useReturn: true,
              otherPiece: 0,
              InvoiceId: null,
              paymentMethod: PaymentMethod.RETURN,
              payDate: new Date(),
              InvoicePaymentMultiple: [],
              InvoicePiece: [],
              note: '',
              BankId: null,
              SalesId: null,
              autorisasi: false,
              totalPay:
                item.totalPrice - (item.totalUsage || 0) <= currentTotalPrice
                  ? item.totalPrice - (item.totalUsage || 0)
                  : item.totalPrice - (item.totalUsage || 0) - diff(currentTotalPrice, item.totalPrice - (item.totalUsage || 0))
            };
          });

          setCurrentTotalReturn(totalReturn);
          setCheckedInvoiceReturn(dataReturn.data.data);
          setCheckedReturn(_checkedReturn);
        }

        if (dataBank?.data?.data) {
          setBank(dataBank.data.data);
          setBanks([dataBank.data.data]);
        }
      })
      .finally(() => {
        setIsLoadingData(false);
        setLoadPartner(false);
        setLoadingBank(false);
      });
  };

  const fetchDeleteReturn = async (id: number) => {
    try {
      await axios.delete(GET_INVOICE_PAYMENT_RETURN_BASE_URL(id));
    } catch (e) {
      console.log('error', e);
    } finally {
      setRefresh(true);
      setPaymentMessageError('MOHON UNTUK MELAKUKAN MUAT ULANG.');
    }
  };

  const handleSearchPartner = async (value: string) => {
    if (value.trim().length < 1) {
      setLoadPartner(false);
      setPartners([dummyPartner]);
      return;
    }

    const params = new URLSearchParams();
    params.append('keyword', value);
    params.append('partnerType', 'CUSTOMER');
    params.append('Zone', (currentUser && currentUser.SalesRoute && currentUser.SalesRoute.map(value => value.ZoneId).join(',')) || '');

    setLoadPartner(true);
    setPartnerMessage('');

    try {
      const { data } = await axios.get(`${PARTNER_SEARCH_BASE_URL}?${params.toString()}`);
      setPartners(data.data);
    } catch (error) {
      console.log('error :', error);
    } finally {
      setLoadPartner(false);
    }
  };

  const handleOnRefresh = () => {
    setCheckedReturn([]);
    setCheckedInvoiceReturn([]);
    setCheckedInvoice([]);
    setUseReturn(false);
    setPaymentMethod('');
    setNote('');
    setPartner(dummyPartner);
    setTotalPay(0);
    setTotalPrice(0);
    setTotalReturn(0);
    setFullReturn(false);
    setCurrentTotalPay(0);
    setCurrentTotalReturn(0);
    setRefresh(false);
    setPaymentMessageError('');

    fecthInvoicePayment();
  };

  const validation = () => {
    let valid = true;

    if (!isFullReturn) {
      if (!totalPay) {
        setTotalPayError('Jumlah Bayar tidak boleh 0.');
        valid = false;
      }

      if (totalPay < 0) {
        setTotalPayError('Jumlah Bayar tidak boleh kurang dari 0.');
        valid = false;
      }

      if (totalPay > totalPrice - totalReturn - otherPiece) {
        setTotalPayError('Jumlah Bayar tidak boleh lebih dari Total Tagihan.');
        valid = false;
      }
    }

    if (paymentMethod === PaymentMethod.BANK_TRANSFER && image.length < 1) {
      setPaymentMethodImageMessage('lampiran tidak boleh kosong.');
      valid = false;
    }

    if (paymentMethod === PaymentMethod.BANK_TRANSFER) {
      const bankTransferErrors: string[] = [];
      if (!bank || bank.id === 0) {
        setBankMessage('Bank tidak boleh kosong.');
        bankTransferErrors.push('Bank tidak boleh kosong.');
        valid = false;
      }

      if (!transferDate) {
        setTransferDateMessage('Tanggal transfer tidak boleh kosong.');
        bankTransferErrors.push('Tanggal transfer tidak boleh kosong.');
        valid = false;
      }

      if (bankTransferErrors.length > 0) {
        setSnackbarVariant('error');
        setMessage(bankTransferErrors.join(' '));
        setOpenSnackbar(true);
      }
    }

    if (paymentMethod === '' || !paymentMethod) {
      setPaymentMethodMessage('Metode Pembayaran tidak boleh kosong.');
      valid = false;
    }

    if (!payDate) {
      setPayDateMessage('Tanggal Pembayaran tidak boleh kosong.');
      valid = false;
    }

    if (!partner || partner.id === 0) {
      setPartnerMessage('Customer tidak boleh kosong');
      valid = false;
    }

    if (checkedInvoice.length === 0) {
      setPaymentMessageError('Invoice tidak boleh kosong');
      valid = false;
    }

    if (useReturn && checkedReturn.length === 0) {
      setPaymentMessageError('Jika menggunakan return mohon pilih invoice return terlebih dahulu');
      valid = false;
    }

    if (paymentMethod === PaymentMethod.GIRO) {
      if (!giroBank) {
        setGiroBankMessage('Bank Penerbit Giro tidak boleh kosong.');
        valid = false;
      }

      if (!giroNumber) {
        setGiroNumberMessage('Nomor Giro tidak boleh kosong.');
        valid = false;
      }

      if (!giroAmount) {
        setGiroAmountMessage('Nominal Giro tidak boleh kosong.');
        valid = false;
      }

      if (giroAmount != totalPay) {
        setGiroAmountMessage('Nominal Giro harus sama dengan Jumlah Tagihan.');
        valid = false;
      }

      if (!giroDueDate) {
        setGiroDueDateMessage('Tanggal Jatuh Tempo Giro tidak boleh kosong.');
        valid = false;
      }

      if (giro.length === 0) {
        setGiroMessage('Data Giro belum dipilih');
        valid = false;
      }
    }

    // if (isFullReturn || paymentMethod === PaymentMethod.RETURN) {
    //   if (totalPrice !== totalReturn) {
    //     setPaymentMessageError('Jika Pembayaran penuh menggunakan RETURN total tagihan harus sesuai dengan jumlah Return');
    //     valid = false;
    //   }
    // }

    return valid;
  };

  const resetValidation = () => {
    setTotalPayError('');
    setPaymentMethodMessage('');
    setPaymentMethodImageMessage('');
    setBankMessage('');
    setTransferDateMessage('');
    setPayDateMessage('');
    setPartnerMessage('');
    setPaymentMessageError('');
    setGiroMessage('');
  };

  const handleSnackBar = (open: boolean, variant: 'success' | 'error', message: string): void => {
    setSnackbarVariant(variant);
    setOpenSnackbar(open);
    setMessage(message);
  };

  const handleCloseSnackbar = (): void => {
    setOpenSnackbar(false);
    handleCloseConfirm();
  };

  const handleConfirmSnackbar = (): void => {
    setOpenSnackbar(false);
    history.push(`/pembayaran-penjualan/${paymentId}`);
  };

  const handleCloseSnackbarGiro = (): void => {
    setOpenSnackbarGiro(false);
  };

  const handleConfirmSnackbarGiro = (): void => {
    setOpenSnackbarGiro(false);
  };

  const handleCancel = () => {
    history.push('/pembayaran-penjualan');
  };

  const individualCheck = (item: { InvoiceId: number; type?: 'invoice' | 'return' }) => {
    let currentChecked = [...checkedInvoice];
    const currentType = item.type ?? 'invoice';
    if (currentChecked.some(value => value.id === item.InvoiceId && (value.type ?? 'invoice') === currentType)) {
      currentChecked = currentChecked.filter(value => !(value.id === item.InvoiceId && (value.type ?? 'invoice') === currentType));
    } else {
      const findInvoice = invoices.find(value => value.id === item.InvoiceId && (value.type ?? 'invoice') === currentType);
      if (findInvoice) {
        currentChecked = [...currentChecked, findInvoice];
      }
    }

    const sorted = sortByInvoiceKey(currentChecked);
    setCheckedInvoice(sorted);
    setCheckedReturn([]);
    setCheckedInvoiceReturn([]);
    setUseReturn(false);
    setFullReturn(false);
    setFullPayment(false);
    setTotalPay(0);

    if (isFullReturn) {
      resetForm();
    }
  };

  const handleReturnInvoiceCheck = (item: InvoiceReturnModel) => {
    handleNotFullPayment();
    let currentCheckedInv = [...checkedInvoiceReturn];
    let currentChecked = [...checkedReturn];
    if (currentCheckedInv.some(value => value.id === item.id)) {
      const _return = checkedReturn.find(_ret => _ret.ReturnId === item.id);
      if (_return && _return.id > 0) {
        fetchDeleteReturn(_return.id);
      }

      currentChecked = currentChecked.filter(_ret => _ret.ReturnId !== item.id);
      currentCheckedInv = currentCheckedInv.filter(value => value.id !== item.id);
    } else {
      const _totalPrice = isUpdate ? totalPrice - totalPay - totalReturn : totalPrice - totalReturn;
      currentCheckedInv = [...currentCheckedInv, item];
      currentChecked = [
        ...currentChecked,
        {
          id: 0,
          paymentNumber: '',
          PaymentId: null,
          ReturnId: item.id,
          useReturn: true,
          otherPiece: 0,
          InvoiceId: 0,
          paymentMethod: PaymentMethod.RETURN,
          payDate: new Date(),
          InvoicePaymentMultiple: [],
          InvoicePiece: [],
          note: '',
          BankId: null,
          SalesId: null,
          autorisasi: false,
          totalPay:
            item.totalPrice - (item.totalUsage || 0) <= _totalPrice
              ? item.totalPrice - (item.totalUsage || 0)
              : item.totalPrice - (item.totalUsage || 0) - diff(_totalPrice, item.totalPrice - (item.totalUsage || 0))
        }
      ];
    }

    const sorted = currentCheckedInv.sort((a, b) => new Date(a.returnDate).getTime() - new Date(b.returnDate).getTime());
    setCheckedInvoiceReturn(sorted);
    setCheckedReturn(currentChecked);
  };

  const handleUseReturn = () => {
    setCheckedInvoiceReturn([]);
    setCheckedReturn([]);

    setUseReturn(prevState => {
      if (prevState && isFullReturn) {
        setPaymentMethod('');
        resetForm();
      }
      return !prevState;
    });

    setFullReturn(false);
    handleNotFullPayment();
  };

  const handleClickOpenInvoiceModal = () => {
    setOpenInvoice(true);
  };

  const handleCloseInvoiceModal = () => {
    setOpenInvoice(false);
  };

  const handleCancelInvoice = () => {
    setCheckedInvoice([]);
    setOpenInvoice(false);
  };

  const handleOnSubmit = () => {
    resetValidation();

    if (!validation()) {
      return;
    }

    handleOpenConfirm();
  };

  const handleDoSubmit = async () => {
    setSubmit(true);
    try {
      const parseReturn =
        checkedReturn.length > 0
          ? checkedReturn.map(value => ({
              id: value.id,
              paymentNumber: value.paymentNumber,
              PaymentId: value.PaymentId,
              ReturnId: value.ReturnId,
              useReturn: value.useReturn,
              InvoiceId: value.InvoiceId,
              paymentMethod: value.paymentMethod,
              payDate: value.payDate,
              note: value.note,
              BankId: value.BankId || null,
              totalPay: value.totalPay
            }))
          : [];

      let payload: any = {
        InvoiceId: checkedInvoice.map(val => ({ id: val.id, type: val.type })),
        BankId: bank.id && bank.id > 0 ? bank.id : null,
        paymentMethod,
        note,
        totalPay,
        useReturn: checkedReturn.length > 0,
        returns: parseReturn,
        payDate,
        otherPiece,
        giroBank,
        transferDate: paymentMethod === PaymentMethod.BANK_TRANSFER ? transferDate : null,
        giroDueDate: paymentMethod === PaymentMethod.GIRO ? format(giroDueDate, 'yyyy-MM-dd') : null,
        image: image || [],
        giroAmount,
        giroNumber,
        PartnerId: partner.id
        // status: paymentMethod === PaymentMethod.GIRO ? PaymentStatus.UNPAID : PaymentStatus.PAID/
      };

      if (isUpdate) {
        payload = { ...payload, id: +id };
      }

      const { data } = await axios.post(INVOICE_PAYMENT_BASE_URL, payload);

      setPaymentId(data.data[0].id);

      if (data.data.length > 0) {
        data.data[0].InvoicePaymentImages.map(async (value: any, index: number) => {
          if (image.filter(img => img.path === value.path).length) {
            const newImageKey = value.path;
            const fileExtension = newImageKey.split('.').pop();
            const myHeaders = new Headers();
            myHeaders.append('Content-Type', `image/${fileExtension}`);

            const config: RequestInit = {
              method: 'PUT',
              headers: myHeaders,
              body: imageBlob[index]
            };

            await fetch(value.url, config)
              .then(response => response.text())
              .then(result => console.log(result))
              .catch(error => console.log('error', error));
          }
        });
      }

      if (data.data.length > 0) {
        handleSnackBar(true, 'success', 'Pembayaran berhasil dibuat');
      } else {
        handleSnackBar(true, 'error', data.message);
      }
    } catch (error) {
      console.log('error :', error);
      handleSnackBar(true, 'error', '500 Error internal server');
    } finally {
      setSubmit(false);
    }
  };

  const handleFullPayment = () => {
    setFullPayment(true);
    setTotalPay(totalPrice - totalReturn - otherPiece);
    setTotalPayError('');
  };

  const handleNotFullPayment = () => {
    setFullPayment(false);
    setTotalPay(0);
    setTotalPayError('');
  };

  const individualCheckGiro = (selectedGiro: GiroModalModel) => {
    setGiro(giro.filter(item => item.giroNumber !== selectedGiro.giroNumber));
    setGiroBank('');
    setGiroNumber('');
    setGiroAmount(0);
    setGiroDueDate(new Date());
    setTotalPay(0);
    setGiroMessage('');
  };

  const handleOpenReturn = () => {
    setOpenReturn(true);
  };

  const handleClickOpenGiroModal = () => {
    setIsCreateGiro(true);
  };

  const handleClickCLoseGiroModal = () => {
    setIsCreateGiro(false);
  };

  const handleCloseReturn = () => {
    setOpenReturn(false);
  };

  const handleOpenConfirm = () => {
    setOpenConfirm(true);
  };

  const handleCloseConfirm = () => {
    setOpenConfirm(false);
  };

  const resetForm = () => {
    setNote('');
    setPaymentMethod('');
    setBank(dummyBank);
    setGiroBank('');
    setGiroNumber('');
    setGiroAmount(0);
    setGiroDueDate(new Date());
    setGiro([]);
  };

  const fetchOtherPaymentsByInv = async (invIds: number[]) => {
    try {
      const { data } = await axios.get(`${INVOICE_PAYMENT_BASE_URL}/other-payment?inv=${invIds.join(',')}`, { cancelToken: cancelTokenSource.token });
      if (data?.data) {
        const op: OtherPayment[] = data.data.map((v: any) => {
          return { ...v, data: v.data.filter((f: any) => (isUpdate ? f.id !== +id : true)) };
        });
        const planTotalPay = op.flatMap(v => v.data).reduce((acc, curr) => acc + curr.planTotalPay, 0);
        const totPrice = checkedInvoice.reduce((acc, curr) => acc + (curr.totalPrice - curr.totalPay), 0);
        setOtherPayments(op);
        setTotalPrice(totPrice - planTotalPay);
      }
    } catch (error) {
      console.log('error: ', error);
    } finally {
    }
  };

  const resetValue = () => {
    setInvoices([]);
    setCheckedReturn([]);
    setCheckedInvoiceReturn([]);
    setCheckedInvoice([]);
    setTotalPay(0);
    setUseReturn(false);
    setFullReturn(false);
    setGiro([]);
  };

  const handleAddGiro = (newGiro: GiroModalModel) => {
    const giroAmountValue = Number(newGiro.giroAmount || 0);
    setGiro([newGiro]);
    setGiroBank(newGiro.giroBank || '');
    setGiroNumber(newGiro.giroNumber || '');
    setGiroAmount(giroAmountValue);
    setGiroDueDate(new Date(newGiro.giroDueDate));
    setTotalPay(giroAmountValue);
    setOpenSnackbarGiro(false);
    setGiroMessage('');
    setFullPayment(false);
  };

  useEffect(() => {
    setTotalPrice(checkedInvoice.reduce((a, b) => a + (b.totalPrice - b.totalPay), 0));
  }, [checkedInvoice]);

  useEffect(() => {
    if (fullPayment) handleFullPayment();
  }, [otherPiece]);

  useEffect(() => {
    if (checkedReturn.length === 0) {
      setTotalReturn(0);
      return;
    }

    const _totalReturn = checkedReturn.map(value => value.totalPay).reduce((a, b) => a + b, 0);
    if (!isFullReturn && _totalReturn === totalPrice) {
      resetForm();
    }

    setTotalReturn(_totalReturn);
    setFullReturn(_totalReturn === totalPrice);

    if (_totalReturn === totalPrice) {
      setTotalPay(_totalReturn);
      setPaymentMethod(PaymentMethod.RETURN);
    } else {
      if (paymentMethod === PaymentMethod.RETURN) {
        setTotalPay(_totalReturn);
      }
    }
  }, [checkedReturn, totalPrice, isUpdate]);

  useEffect(() => {
    if (!id) return;
    fecthInvoicePayment();
  }, [id]);

  useEffect(() => {
    if (checkedInvoice.length === 0) return;
    fetchOtherPaymentsByInv(checkedInvoice.map(v => v.id));
  }, [checkedInvoice, isUpdate]);

  useEffect(() => {
    if (!partner) return;
    fetchCheckAutorisasi();
    if (partner.id < 1) {
      if (isFullReturn) {
        resetForm();
        resetValue();
      } else {
        resetValue();
      }
    }
  }, [partner]);

  useEffect(() => {
    setDisableMethod(false);
    if (isFullReturn === true) {
      setDisableMethod(true);
    }
  }, [isFullReturn]);

  useEffect(() => {
    if (!openInvoice) return;
    const debouncedFetchHits = _.debounce(fetchData, 500);
    debouncedFetchHits();
    return () => cancelTokenSource.cancel('No longer latest query');
  }, [queryString, orderBy, order, currentPage, openInvoice, isUpdate]);

  useEffect(() => {
    if (!openReturn) return;
    const debouncedFetchHits = _.debounce(fetchReturnInvoice, 500);
    debouncedFetchHits();
    return () => cancelTokenSource.cancel('No longer latest query');
  }, [openReturn, returnNumber]);

  useEffect(() => {
    if (partnerName.trim().length < 1) {
      setLoadPartner(false);
      setPartners([dummyPartner]);
      return;
    }

    const timer = setTimeout(() => {
      handleSearchPartner(partnerName);
    }, 500);

    return () => clearTimeout(timer);
  }, [partnerName]);

  useEffect(() => {
    const exists = otherPayments
      .flatMap(v => v.data)
      .filter(f => f.paymentMethod === paymentMethod && f.autorisasi)
      .map(v => v.invoiceId);
    setInvoicePending(exists);
  }, [otherPayments, paymentMethod, checkedInvoice]);

  return (
    <Page title={isUpdate ? 'Ubah Pembayaran Penjualan' : 'Buat Pembayaran Penjualan'}>
      <Container>
        <Grid container direction='row'>
          <Grid item lg={12} md={12} sm={12} xs={12}>
            <Typography variant='h1'>{isUpdate ? 'Ubah Pembayaran Penjualan' : 'Buat Pembayaran Penjualan'}</Typography>
          </Grid>
          <Grid item lg={12} md={12} sm={12} xs={12}>
            <Breadcrumb />
          </Grid>
        </Grid>

        {hasAutorisasi ? (
          <Alert severity='warning'>
            <AlertTitle>Autorisasi</AlertTitle>
            Pengguna ini memiliki invoice yang masih menunggu otorisasi. <br />
            Silakan selesaikan proses otorisasi terlebih dahulu sebelum melakukan pembayaran.
          </Alert>
        ) : null}

        {isUpdate && !isLoadingData && !payload ? <Alert severity='warning'>Edit Pembayaran untuk Kwitansi ini tidak tersedia.</Alert> : null}

        <PaperCustom>
          <Grid container direction='row' spacing={2}>
            <Grid container direction='row' spacing={1} item xl={12} lg={12} md={12} sm={12} xs={12}>
              <Grid item xl={6} lg={6} md={12} sm={12} xs={12}>
                <Autocomplete
                  id='partner'
                  disabled={isUpdate}
                  value={partner}
                  options={partners}
                  getOptionLabel={option => option.name}
                  getOptionSelected={(option, value) => {
                    return value.id === option.id;
                  }}
                  onChange={(event: any, value: any) => {
                    if (value) {
                      setPartner(value);
                      resetValue();
                    }
                  }}
                  onOpen={e => setPartnerName('')}
                  onInputChange={(event: React.ChangeEvent<{}>, value: string, reason: AutocompleteInputChangeReason) => {
                    if (reason === 'clear') {
                      setPartner(dummyPartner);
                      setPartners([dummyPartner]);
                    }
                  }}
                  loading={loadPartner}
                  renderOption={(option, state) => (
                    <CardCustomer partner={option} isLoading={loadPartner} isSelected={option.id === partner.id && !loadPartner} />
                  )}
                  renderInput={params => (
                    <TextField
                      {...params}
                      required
                      label='Pilih Customer'
                      onChange={e => {
                        setPartnerName(e.target.value);
                      }}
                      error={partnerMessage !== ''}
                      helperText={partnerMessage}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <Fragment>
                            {loadPartner && <CircularProgress color='inherit' size={20} />}
                            {params.InputProps.endAdornment}
                          </Fragment>
                        )
                      }}
                    />
                  )}
                />
              </Grid>

              <Grid
                item
                xl={paymentMethod === PaymentMethod.BANK_TRANSFER || paymentMethod === PaymentMethod.GIRO ? 3 : 6}
                lg={paymentMethod === PaymentMethod.BANK_TRANSFER || paymentMethod === PaymentMethod.GIRO ? 3 : 6}
                md={paymentMethod === PaymentMethod.BANK_TRANSFER || paymentMethod === PaymentMethod.GIRO ? 3 : 6}
                xs={12}
                sm={12}
              >
                <TextField
                  id='paymentMethod'
                  required
                  label='Metode Pembayaran'
                  disabled={disableMethod}
                  value={paymentMethod as string}
                  error={paymentMethodMessage !== ''}
                  helperText={paymentMethodMessage}
                  onChange={e => {
                    resetForm();
                    setPaymentMethod(e.target.value as string);
                    setFullReturn(e.target.value === PaymentMethod.RETURN);
                  }}
                  select
                  fullWidth
                >
                  <MenuItem key={'1'}>Metode Pembayaran</MenuItem>
                  {isFullReturn ? (
                    <MenuItem key={'2'} value={PaymentMethod.RETURN}>
                      {PaymentMethod.RETURN}
                    </MenuItem>
                  ) : (
                    Object.entries(PaymentMethod).map(([key, value]) => (
                      <MenuItem key={key} value={value}>
                        {value}
                      </MenuItem>
                    ))
                  )}
                </TextField>
              </Grid>

              {paymentMethod === PaymentMethod.BANK_TRANSFER && (
                <Grid item xl={3} lg={3} md={3} xs={12} sm={12}>
                  <Autocomplete
                    id='bank'
                    value={bank}
                    options={banks.filter(v => [2, 3, 4].includes(v.id))}
                    getOptionLabel={option => option.bankName}
                    getOptionSelected={(option, value) => {
                      return value.id === option.id;
                    }}
                    onChange={({}, value: any) => {
                      if (value) {
                        setBank(value);
                        setNote(`Transfer Rekening: (${value.bankCode}) ${value.bankName} - No Rek : ${value.bankNumber} / ${value.bankAccount}`);
                      }
                    }}
                    onOpen={e => handleSearchBank('')}
                    loading={loadingBank}
                      renderInput={params => (
                        <TextField
                          {...params}
                          required
                          label='Pilih Bank'
                          onChange={e => handleSearchBank(e.target.value)}
                          error={bankMessage !== ''}
                          helperText={bankMessage}
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <Fragment>
                                {loadingBank && <CircularProgress color='inherit' size={20} />}
                              {params.InputProps.endAdornment}
                            </Fragment>
                          )
                        }}
                      />
                    )}
                  />
                </Grid>
              )}

              {paymentMethod === PaymentMethod.GIRO && (
                <Grid item xl={3} lg={3} md={3} xs={12} sm={12}>
                  <TextField
                    id='bank'
                    required
                    fullWidth
                    type='text'
                    label='Bank Penerbit'
                    placeholder=''
                    value={giroBank}
                    onChange={e => setGiroBank(e.target.value)}
                    error={giroBankMessage !== ''}
                    helperText={giroBankMessage}
                    InputLabelProps={{
                      shrink: true
                    }}
                  />
                </Grid>
              )}

              <Grid item xl={6} lg={6} md={12} sm={12} xs={12}>
                {paymentMethod === PaymentMethod.BANK_TRANSFER && (
                    <TextField
                      id='payDate'
                      required
                      fullWidth
                      type='date'
                      label='Tanggal Transfer'
                      placeholder=''
                      value={format(transferDate, 'yyyy-MM-dd')}
                      onChange={e => setTransferDate(new Date(e.target.value))}
                      error={transferDateMessage !== ''}
                      helperText={transferDateMessage}
                      InputLabelProps={{
                        shrink: true
                      }}
                    />
                  )}
                <TextField
                  id='payDate'
                  required
                  fullWidth
                  type='date'
                  label='Tanggal Kwitansi'
                  placeholder=''
                  value={format(payDate, 'yyyy-MM-dd')}
                  onChange={e => setPayDate(new Date(e.target.value))}
                  error={payDateMessage !== ''}
                  helperText={payDateMessage}
                  InputLabelProps={{
                    shrink: true
                  }}
                />
              </Grid>

              {paymentMethod === PaymentMethod.GIRO && (
                <Grid item xl={3} lg={3} md={3} xs={12} sm={12}>
                  <TextField
                    id='bank'
                    required
                    fullWidth
                    type='text'
                    label='Nomor Giro'
                    placeholder=''
                    value={giroNumber}
                    onChange={e => setGiroNumber(e.target.value)}
                    error={giroNumberMessage !== ''}
                    helperText={giroNumberMessage}
                    InputLabelProps={{
                      shrink: true
                    }}
                  />
                </Grid>
              )}

              {paymentMethod === PaymentMethod.GIRO && (
                <Grid item xl={3} lg={3} md={3} xs={12} sm={12}>
                  <TextField
                    id='bank'
                    required
                    fullWidth
                    type='text'
                    label='Nominal Giro'
                    placeholder=''
                    value={giroAmount}
                    onChange={e => setGiroAmount(+e.target.value)}
                    error={giroAmountMessage !== ''}
                    helperText={giroAmountMessage}
                    InputProps={{ inputComponent: NumberFormatMask as any }}
                    InputLabelProps={{
                      shrink: true
                    }}
                  />
                </Grid>
              )}

              {paymentMethod === PaymentMethod.GIRO && (
                <Grid container spacing={1}>
                  <Grid item xl={6} lg={6} md={6} xs={12} sm={12}>
                    &nbsp;
                  </Grid>
                  <Grid item xl={6} lg={6} md={6} xs={12} sm={12}>
                    <TextField
                      id='giroDueDate'
                      required
                      fullWidth
                      type='date'
                      label='Tanggal Jatuh Tempo Giro'
                      placeholder=''
                      value={format(giroDueDate, 'yyyy-MM-dd')}
                      onChange={e => setGiroDueDate(new Date(e.target.value))}
                      error={giroDueDateMessage !== ''}
                      helperText={giroDueDateMessage}
                      InputLabelProps={{
                        shrink: true
                      }}
                    />
                  </Grid>
                </Grid>
              )}

              {paymentMethod === PaymentMethod.GIRO && (
                <Grid item xl={6} lg={6} md={6} xs={12} sm={12}>
                  &nbsp;
                </Grid>
              )}

              {paymentMethod === PaymentMethod.GIRO && (
                <Grid item xl={12} lg={12} md={12} sm={12} xs={12}>
                  <GiroTable giro={giro} handleIndividualCheck={individualCheckGiro} handleEditGiro={handleClickOpenGiroModal} />
                  {giroMessage !== '' && <FormHelperText error>{giroMessage}</FormHelperText>}
                </Grid>
              )}

              {paymentMethod === PaymentMethod.GIRO && (
                <Grid item xl={12} lg={12} md={12} sm={12} xs={12}>
                  <Button disabled={partner.id === 0 || checkedInvoice.length === 0} variant='outlined' onClick={handleClickOpenGiroModal}>
                    {giro.length > 0 ? 'Edit Giro' : 'Tambah Giro'}
                  </Button>
                </Grid>
              )}

              <Grid item xl={6} lg={6} md={12} sm={12} xs={12}>
                <TextField fullWidth id='note' label='Catatan' multiline rows={4} value={note} onChange={e => setNote(e.target.value)} />
              </Grid>
              {/* {paymentMethod === PaymentMethod.BANK_TRANSFER && imageUrl.length > 0 && ( */}
              {paymentMethod === PaymentMethod.BANK_TRANSFER && (
                <Grid xs={12} className={classes.warningWrapper}>
                  <Grid className={classes.warningContent} xs={12}>
                    <Typography variant='h6' style={{ borderBottom: '1.5px solid ' + GREY }}>
                      Harap di Perhatikan!
                    </Typography>

                    <Typography style={{ fontSize: '1em' }}>
                      Mohon pastikan nominal bukti transfer sama dengan jumlah total tagihan nota yang akan dibayar saat ini.
                    </Typography>
                  </Grid>
                </Grid>
              )}
              {paymentMethod === PaymentMethod.BANK_TRANSFER && (
                <Grid xs={12} {...getRootProps({ className: classes.dropZone })}>
                  {imageUrl.length < 3 && <input {...getInputProps()} />}
                  {imageUrl.length > 0 ? (
                    <aside className={classes.thumbsContainer}>
                      {imageUrl.map((value, index) => (
                        <div className={classes.thumb} key={index}>
                          <div className={classes.thumbInner}>
                            <img src={value} key={index} className={classes.img} />
                          </div>
                          <CloseRounded className={classes.closeIcon} color='error' onClick={() => handleDeleteImage(index)} />
                        </div>
                      ))}
                      {imageUrl.length > 0 && imageUrl.length < 3 && <AddRounded className={classes.addIcon} color='primary' />}
                    </aside>
                  ) : (
                    <>
                      <CloudUpload className={classes.iconUpload} />
                      <Typography align='center'> Geser dan lepaskan disini untuk mengupload gambar PNG, JPEG, JPG, Max 5 *</Typography>
                    </>
                  )}
                </Grid>
              )}

              {paymentMethodImageMessage !== '' && <Typography color='error'>{paymentMethodImageMessage}</Typography>}
            </Grid>

            <Grid container direction='row' item xl={12} md={12} sm={12}>
              <FormHelperText error={paymentMessageError !== ''}>{paymentMessageError}</FormHelperText>

              <InvoiceTable
                isLoadingData={isLoadingData}
                isUpdate={isUpdate}
                checkedInvoice={checkedInvoice}
                totalPay={isFullReturn ? totalPay - totalReturn : currentTotalPay + currentTotalReturn - totalPay - totalReturn}
                handleIndividualCheck={individualCheck}
                otherPayments={otherPayments}
                invoicePending={invoicePending}
                payload={payload}
              />
            </Grid>

            <Grid container direction='row' item xl={12} md={12} sm={12} spacing={1}>
              <Grid item>
                <Button disabled={partner.id === 0 || isUpdate} variant='outlined' onClick={handleClickOpenInvoiceModal}>
                  Pilih Invoice Rujukan
                </Button>
              </Grid>

              <Grid item>
                {isUpdate && (
                  <Button onClick={handleOnRefresh} className={classes.refresh}>
                    Memuat Ulang
                  </Button>
                )}
              </Grid>
            </Grid>

            <Grid item lg={12} sm={12} md={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    disabled={checkedInvoice.length === 0}
                    checked={useReturn}
                    onChange={handleUseReturn}
                    color='primary'
                  />
                }
                label={`Gunakan Invoice Return ${(partner.totalReturn && partner.totalReturn > 0 && '(Customer Ini memiliki Jumlah Return)') || ''}`}
              />
            </Grid>

            <Fade in={useReturn}>
              <Grid container direction='row' item xl={12} md={12} sm={12}>
                <InvoiceReturnTable
                  isLoadingData={isLoadingData}
                  checked={checkedInvoiceReturn}
                  invoicePayment={checkedReturn}
                  handleIndividualCheck={handleReturnInvoiceCheck}
                />
              </Grid>
            </Fade>

            <Fade in={useReturn}>
              <Grid container direction='row' item xl={12} md={12} sm={12} spacing={1}>
                <Grid item>
                  <Button disabled={!useReturn} variant='outlined' onClick={handleOpenReturn}>
                    Tambahkan Return
                  </Button>
                </Grid>
              </Grid>
            </Fade>

            <Grid container direction='row' justify='flex-end' alignItems='center' alignContent='center' item xl={12} md={12} sm={12}>
              <Grid item>
                <Typography variant='subtitle2'>Jumlah Tagihan :</Typography>
              </Grid>

              <Grid item>
                <NumberFormat value={totalPrice} prefix={'Rp'} thousandSeparator={true} displayType='text' />
              </Grid>
            </Grid>

            <Grid container direction='row' justify='flex-end' alignItems='center' alignContent='center' item xl={12} md={12} sm={12}>
              <Grid item>
                <Typography variant='subtitle2'>Jumlah Return :</Typography>
              </Grid>

              <Grid item>
                <NumberFormat value={totalReturn} prefix={'-Rp'} thousandSeparator={true} displayType='text' />
              </Grid>
            </Grid>
            <Grid container direction='row' justify='flex-end' alignItems='center' alignContent='center' item xl={12} md={12} sm={12}>
              <TextField
                label='Potongan Lainya'
                fullWidth={isSmall}
                //disabled={fullPayment}
                value={otherPiece > 0 ? otherPiece : ''}
                onChange={e => {
                  setOtherPiece(+e.target.value);
                }}
                InputProps={{
                  inputComponent: NumberFormatMask as any
                }}
              />
            </Grid>

            <Grid container direction='row' justify='flex-end' alignItems='center' alignContent='center' item xl={12} md={12} sm={12}>
              <Grid item>
                <Typography variant='subtitle2'>Total Tagihan :</Typography>
              </Grid>
              <Grid item>
                <NumberFormat value={totalPrice - totalReturn - otherPiece} prefix={'Rp'} thousandSeparator={true} displayType='text' />
              </Grid>
            </Grid>

            <Grid container direction='row' justify='flex-end' alignItems='center' alignContent='center' item xl={12} md={12} sm={12}>
              <Fade in={!isFullReturn}>
                <RadioGroup row aria-label='position' name='position' defaultValue='end'>
                  <FormControlLabel
                    onChange={handleFullPayment}
                    value='1'
                    control={<Radio color='primary' checked={fullPayment} />}
                    label='Bayar Full'
                    labelPlacement='end'
                  />
                  <FormControlLabel
                    onChange={handleNotFullPayment}
                    value='0'
                    control={<Radio color='primary' checked={!fullPayment} />}
                    label='Bayar Sebagian'
                    labelPlacement='end'
                  />
                </RadioGroup>
              </Fade>
            </Grid>

            <Grid container direction='row' justify='flex-end' alignItems='center' alignContent='center' item xl={12} md={12} sm={12}>
              <Fade in={!isFullReturn}>
                <TextField
                  label='Jumlah Bayar'
                  fullWidth={isSmall}
                  disabled={fullPayment}
                  value={totalPay > 0 ? totalPay : ''}
                  helperText={totalPayError}
                  error={totalPayError !== ''}
                  onChange={e => {
                    setTotalPay(+e.target.value);
                  }}
                  InputProps={{
                    inputComponent: NumberFormatMask as any
                  }}
                />
              </Fade>
            </Grid>

            <Grid container direction='row' justify='flex-end' alignItems='center' alignContent='center' item xl={12} md={12} sm={12}>
              <Grid item>
                <Typography variant='subtitle2'>Total Bayar :</Typography>
              </Grid>

              <Grid item>
                <NumberFormat value={totalPay + otherPiece} prefix={'Rp'} thousandSeparator={true} displayType='text' />
              </Grid>
            </Grid>

            <Grid container direction='row' justify={isSmall ? 'space-between' : 'flex-end'} spacing={2} item xl={12} md={12} sm={12}>
              <Grid item xl={2} lg={2} md={12} sm={12} xs={12}>
                <Button variant={isSmall ? 'outlined' : 'contained'} onClick={handleCancel} fullWidth style={{ color: '#bdbdbd' }} color='inherit'>
                  Batal
                </Button>
              </Grid>
              <Grid item xl={2} lg={2} md={12} sm={12} xs={12}>
                <Button
                  disabled={isSubmit || isRefresh || (isUpdate && !payload) || (!isUpdate && invoicePending.length > 0)}
                  fullWidth
                  onClick={handleOnSubmit}
                >
                  {isSubmit ? <CircularProgress color='inherit' size={20} /> : 'Simpan'}
                </Button>
              </Grid>
            </Grid>

            <StandardConfirmationDialog
              variant={snackbarVariant}
              titleMessage={snackbarVariant === 'success' ? 'Success!' : 'Error!'}
              message={message}
              open={openSnackbar}
              handleClose={snackbarVariant === 'success' ? handleConfirmSnackbar : handleCloseSnackbar}
              onConfirm={handleConfirmSnackbar}
              noCancelButton={true}
            />
            <StandardConfirmationDialogGiro
              variant={snackbarVariant}
              titleMessage={snackbarVariant === 'success' ? 'Success!' : 'Error!'}
              message={message}
              open={openSnackbarGiro}
              handleClose={handleCloseSnackbarGiro}
              onConfirm={handleConfirmSnackbarGiro}
              noCancelButton={true}
            />
            <GiroModal
              handleAddGiro={handleAddGiro}
              giro={giro}
              open={isCreateGiro}
              handleCloseCreate={handleClickCLoseGiroModal}
              initialGiro={giro[0] || null}
              bankInputMode='hybrid'
            />

            <InvoiceModal
              checked={checkedInvoice}
              openInvoice={openInvoice}
              queryString={queryString}
              paymentMessageError={paymentMessageError}
              order={order}
              orderBy={orderBy}
              isLoadingData={isLoadingData}
              invoices={invoices}
              count={count}
              currentPage={currentPage}
              handleClose={handleCloseInvoiceModal}
              setQueryString={setQueryString}
              handleRequestSort={handleRequestSort}
              setCurrentPage={setCurrentPage}
              handleIndividualCheck={individualCheck}
              handleCancelInvoice={handleCancelInvoice}
            />

            <InvoiceReturnModal
              checked={checkedInvoiceReturn}
              openInvoice={openReturn}
              paymentMessageError={''}
              isLoadingData={isLoadingReturn}
              invoices={invoiceReturn}
              queryString={returnNumber}
              setQueryString={setReturnNumber}
              handleClose={handleCloseReturn}
              handleIndividualCheck={handleReturnInvoiceCheck}
            />

            <ConfirmPaymentModal
              open={openConfirm}
              editedPaymentId={+(id || 0)}
              isLoading={isSubmit}
              handleClose={handleCloseConfirm}
              transferDate={transferDate}
              handleConfirm={handleDoSubmit}
              invoice={checkedInvoice}
              invoiceReturn={checkedInvoiceReturn}
              payDate={payDate}
              bankId={bank.id}
              otherPiece={otherPiece}
              bankName={bank.bankName}
              partnerName={partner.name}
              paymentMethod={paymentMethod}
              note={note}
              totalPay={totalPay}
              totalPrice={totalPrice}
              totalReturn={totalReturn}
              useReturn={checkedReturn.length > 0}
              giroBank={giroBank}
              giroDueDate={giroDueDate}
              giroAmount={giroAmount}
              giroNumber={giroNumber}
            />
          </Grid>
        </PaperCustom>
      </Container>
    </Page>
  );
};

export default PaymentCreatePage;
